import { z } from 'zod'
import { HouseholdId, RecipeId, UserId } from './ids.ts'

// Recipes are family-scoped — no `ownerId`. Anyone in the household can
// view, edit, and (eventually) delete any recipe.

// Tag enums. These are the canonical lists; the tag-inference Lambda
// (`server/src/handlers/recipe/inferTags.ts`) imports them so the prompt
// enum listing, the validation schema and the persisted shape never drift.
// Order matches the meal-planner suggestion algorithm draft.

export const PRIMARY_PROTEIN = [
	'chicken',
	'red_meat',
	'fish_white',
	'beans_legumes',
	'none',
	'other',
	'unknown',
] as const
export const PrimaryProtein = z.enum(PRIMARY_PROTEIN)
export type PrimaryProtein = z.infer<typeof PrimaryProtein>

export const PRIMARY_CARB = [
	'rice',
	'pasta',
	'potato',
	'bread_wrap',
	'noodles',
	'grains',
	'none',
	'other',
	'unknown',
] as const
export const PrimaryCarb = z.enum(PRIMARY_CARB)
export type PrimaryCarb = z.infer<typeof PrimaryCarb>

export const SAUCE_BASE = [
	'tomato',
	'cream',
	'coconut',
	'soy_umami',
	'broth_stock',
	'dry_spiced',
	'dressing_based',
	'none',
	'other',
	'unknown',
] as const
export const SauceBase = z.enum(SAUCE_BASE)
export type SauceBase = z.infer<typeof SauceBase>

export const CUISINE = [
	'italian',
	'indian',
	'chinese',
	'south_american',
	'texan',
	'malaysian',
	'singaporean',
	'thai',
	'mexican',
	'middle_eastern',
	'spanish',
	'french',
	'british',
	'american',
	'scandinavian',
	'japanese',
	'european',
	'east_european',
	'other',
] as const
export const Cuisine = z.enum(CUISINE)
export type Cuisine = z.infer<typeof Cuisine>

export const SEASON = ['all_year', 'summer', 'winter'] as const
export const Season = z.enum(SEASON)
export type Season = z.infer<typeof Season>

// Meal the recipe is typically eaten as. `unknown` when the model can't tell.
export const RECIPE_TYPE = [
	'breakfast',
	'lunch',
	'dinner',
	'dessert',
	'snack',
	'unknown',
] as const
export const RecipeType = z.enum(RECIPE_TYPE)
export type RecipeType = z.infer<typeof RecipeType>

// Hands-on work and skill required, independent of total cooking time. A
// one-pan traybake is low effort even if it roasts for an hour; a laminated
// pastry is high effort even if it's quick to bake. `unknown` when unclear.
export const EFFORT = ['low', 'medium', 'high', 'unknown'] as const
export const Effort = z.enum(EFFORT)
export type Effort = z.infer<typeof Effort>

// Whether the dish scales up and keeps, so one big cook covers a later meal.
// The meal planner weights these towards Sat–Sun, where there's time for it.
export const BATCH_COOK = ['yes', 'no', 'unknown'] as const
export const BatchCook = z.enum(BATCH_COOK)
export type BatchCook = z.infer<typeof BatchCook>

export const SystemTags = z.object({
	primaryProtein: PrimaryProtein,
	primaryCarb: PrimaryCarb,
	sauceBase: SauceBase,
	cuisine: Cuisine,
	season: Season,
	// `type`, `effort` and `batchCook` post-date the original tagger. Older
	// stored rows lack them, so they default to 'unknown' rather than failing
	// the parse; the (now-removed) `prepSpeed` key on those rows is simply
	// stripped.
	type: RecipeType.default('unknown'),
	effort: Effort.default('unknown'),
	batchCook: BatchCook.default('unknown'),
	// Total minutes (prep + cook). Omitted when the model can't tell.
	prepTime: z.number().int().positive().optional(),
})
export type SystemTags = z.infer<typeof SystemTags>

// pending  — fresh ingest, tagger has been enqueued
// tagged   — tagger finished and wrote systemTags back
// failed   — tagger gave up (Bedrock error or invalid output); systemTags
//            stays null and the UI hides the pill. "Best-effort" — the
//            recipe is still fully usable.
export const TaggingStatus = z.enum(['pending', 'tagged', 'failed'])
export type TaggingStatus = z.infer<typeof TaggingStatus>

export const RecipeTitle = z.string().trim().min(1).max(200)
export type RecipeTitle = z.infer<typeof RecipeTitle>

export const RecipeIngredient = z.string().trim().min(1).max(500)
export type RecipeIngredient = z.infer<typeof RecipeIngredient>

export const RecipeInstruction = z.string().trim().min(1).max(2000)
export type RecipeInstruction = z.infer<typeof RecipeInstruction>

// Ingredients and instructions are grouped into sections so a multi-part
// recipe ("For the sauce" / "For the topping") keeps its structure instead of
// collapsing into one flat list. `heading: null` is the default, unnamed group
// — a plain single-list recipe is one such group.
export const RecipeSectionHeading = z.string().trim().min(1).max(80)
export type RecipeSectionHeading = z.infer<typeof RecipeSectionHeading>

export const IngredientSection = z.object({
	heading: RecipeSectionHeading.nullable(),
	items: z.array(RecipeIngredient).min(1),
})
export type IngredientSection = z.infer<typeof IngredientSection>

export const InstructionSection = z.object({
	heading: RecipeSectionHeading.nullable(),
	items: z.array(RecipeInstruction).min(1),
})
export type InstructionSection = z.infer<typeof InstructionSection>

// Rows created before sections existed (and every scraped recipe) store plain
// `string[]`. Upcast those to a single unnamed section on read so stored data
// and the scrape path need no migration. Already-sectioned values pass through.
const asSections = (v: unknown): unknown =>
	Array.isArray(v) && typeof v[0] === 'string'
		? [{ heading: null, items: v }]
		: v

export const Recipe = z.object({
	id: RecipeId,
	householdId: HouseholdId,
	// null for hand-authored recipes; the scraped source page otherwise.
	sourceUrl: z.url().nullable().default(null),
	title: RecipeTitle,
	ingredients: z.preprocess(asSections, z.array(IngredientSection).min(1)),
	instructions: z.preprocess(asSections, z.array(InstructionSection)),
	// null when there's no image (source page had none / upload failed).
	// Otherwise the canonical S3 URL, carrying a `?v=` cache-buster. The
	// bucket is private: the client never renders this directly — it hits the
	// authed, household-scoped `GET /recipes/{id}/image` proxy instead. Set by
	// scrape ingest or a user's custom upload/replace.
	imageUrl: z.url().nullable(),
	// Rows created before these attributes existed lack them entirely; default
	// to null so they still parse. `.default(null)` keeps the value `number |
	// null` (never undefined) for downstream code like totalMins().
	prepTimeMins: z.number().int().nonnegative().nullable().default(null),
	cookTimeMins: z.number().int().nonnegative().nullable().default(null),
	servings: z.number().int().positive().nullable().default(null),
	systemTags: SystemTags.nullable(),
	taggingStatus: TaggingStatus,
	createdAt: z.iso.datetime(),
	createdBy: UserId,
})
export type Recipe = z.infer<typeof Recipe>

// Flatten sectioned ingredients back to plain lines for the readers that don't
// care about grouping — full-text search and the tagger prompt.
export const flatIngredients = (r: Recipe): string[] =>
	r.ingredients.flatMap((s) => s.items)

export const RecipeListResponse = z.object({
	recipes: z.array(Recipe),
})
export type RecipeListResponse = z.infer<typeof RecipeListResponse>

// Two ways in, one endpoint: paste a URL to scrape, or hand-author the whole
// recipe. The manual branch carries the fields the scraper would otherwise
// fill; tags are still inferred asynchronously, so they're not sent here.
export const RecipeCreateBody = z.discriminatedUnion('kind', [
	z.object({ kind: z.literal('url'), url: z.url() }),
	z.object({
		kind: z.literal('manual'),
		title: RecipeTitle,
		ingredients: z.array(IngredientSection).min(1),
		instructions: z.array(InstructionSection).default([]),
		prepTimeMins: z.number().int().nonnegative().nullable().default(null),
		cookTimeMins: z.number().int().nonnegative().nullable().default(null),
		servings: z.number().int().positive().nullable().default(null),
	}),
])
export type RecipeCreateBody = z.infer<typeof RecipeCreateBody>

export const RecipeCreateResponse = z.object({ recipe: Recipe })
export type RecipeCreateResponse = z.infer<typeof RecipeCreateResponse>

// Manual edits. Rename the recipe, override the full systemTags object (small
// enough to send whole; partial updates would need careful merge semantics with
// the async tagger writes), or edit the hand-authored body (ingredients,
// instructions, times, servings). Omit a key to leave it alone.
export const RecipeUpdateBody = z
	.object({
		title: RecipeTitle.optional(),
		systemTags: SystemTags.optional(),
		ingredients: z.array(IngredientSection).min(1).optional(),
		instructions: z.array(InstructionSection).optional(),
		prepTimeMins: z.number().int().nonnegative().nullable().optional(),
		cookTimeMins: z.number().int().nonnegative().nullable().optional(),
		servings: z.number().int().positive().nullable().optional(),
	})
	.refine((b) => Object.values(b).some((v) => v !== undefined), {
		message: 'At least one field must be provided',
	})
export type RecipeUpdateBody = z.infer<typeof RecipeUpdateBody>

export const RecipeUpdateResponse = z.object({ recipe: Recipe })
export type RecipeUpdateResponse = z.infer<typeof RecipeUpdateResponse>

export const RecipeDeleteResponse = z.object({ ok: z.literal(true) })
export type RecipeDeleteResponse = z.infer<typeof RecipeDeleteResponse>
